import {Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ToasterService} from 'angular2-toaster';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {TranslocoService} from '@jsverse/transloco';

interface NotificationItem {
    rid: string;
    notifyDate: number;
    category: string;
    subject: string;
    content: string;
    dismissed?: boolean;
    safeContent?: SafeHtml;
}

/**
 * kobe-ac ダッシュボード。自分宛ての通知 (お知らせ) をカテゴリ別のタブで、本文は HTML 表示する。
 * 本文は管理者が TinyMCE で編集した信頼できる HTML なので DomSanitizer で bypass して整形を保つ。
 *
 * <p>件数が積み上がっても画面を占有しないよう一覧は高さ固定 (テンプレート側) でスクロールする。
 * ユーザは各通知を「非表示」にでき、その状態はサーバ (AC_NotificationDismissal) に保存される
 * (全端末で同期)。「非表示の通知を表示」で再表示・再表示解除できる。管理者が無効化した通知は
 * そもそも /mine に含まれない。
 */
@Component({
    standalone: false,
    selector: 'kobeac-dashboard',
    templateUrl: 'dashboard.component.html',
})
export class KobeacDashboardComponent implements OnInit {
    private base = ActivePromotionConfig.SERVER_URL + '/rest/kobeac/notification';
    notifications: NotificationItem[] = [];

    /** タブ: 先頭は「すべて」、以降は出現順のカテゴリ。 */
    tabs: string[] = ['すべて'];
    activeTab = 'すべて';
    /** 非表示にした通知も表示するか。 */
    showHidden = false;

    constructor(private http: HttpClient, private sanitizer: DomSanitizer, private toaster: ToasterService,
                private transloco: TranslocoService) {
    }

    ngOnInit(): void {
        this.http.get(this.base + '/mine').subscribe({
            next: (res: any) => {
                const items: NotificationItem[] = (res && res.items) ? res.items : [];
                for (const n of items) {
                    if (!n.category) {
                        n.category = 'お知らせ';
                    }
                    n.safeContent = this.sanitizer.bypassSecurityTrustHtml(n.content || '');
                }
                this.notifications = items;
                this.buildTabs();
            },
            error: () => {
                this.notifications = [];
                this.tabs = ['すべて'];
                this.activeTab = 'すべて';
            },
        });
    }

    /** 表示対象 (非表示を除く。ただし showHidden 時は非表示も含む) の通知。 */
    private shown(): NotificationItem[] {
        return this.notifications.filter((n) => this.showHidden || !n.dismissed);
    }

    /** 表示対象からカテゴリタブを組み立てる (新しい順の出現順)。 */
    private buildTabs(): void {
        const cats: string[] = [];
        for (const n of this.shown()) {
            const c = n.category || 'お知らせ';
            if (cats.indexOf(c) < 0) {
                cats.push(c);
            }
        }
        this.tabs = ['すべて'].concat(cats);
        if (this.tabs.indexOf(this.activeTab) < 0) {
            this.activeTab = 'すべて';
        }
    }

    selectTab(t: string): void {
        this.activeTab = t;
    }

    /** 「非表示の通知を表示」トグル。 */
    toggleShowHidden(): void {
        this.showHidden = !this.showHidden;
        this.buildTabs();
    }

    /** 非表示にした通知の件数。 */
    get hiddenCount(): number {
        return this.notifications.filter((n) => n.dismissed).length;
    }

    /** 選択中タブの表示対象通知 (「すべて」は全件)。 */
    get visibleNotifications(): NotificationItem[] {
        const base = this.shown();
        if (this.activeTab === 'すべて') {
            return base;
        }
        return base.filter((n) => (n.category || 'お知らせ') === this.activeTab);
    }

    /** タブごとの件数バッジ用 (表示対象のみ)。 */
    countOf(tab: string): number {
        const base = this.shown();
        if (tab === 'すべて') {
            return base.length;
        }
        return base.filter((n) => (n.category || 'お知らせ') === tab).length;
    }

    /** この通知を非表示にする (サーバ保存)。 */
    dismiss(n: NotificationItem): void {
        this.http.post(this.base + '/dismiss', {rid: n.rid}).subscribe({
            next: () => {
                n.dismissed = true;
                this.buildTabs();
            },
            error: () => this.toaster.pop('error', this.transloco.translate('kobeac.notices'), this.transloco.translate('kobeac.hide_failed')),
        });
    }

    /** 非表示を解除して再表示する。 */
    undismiss(n: NotificationItem): void {
        this.http.post(this.base + '/undismiss', {rid: n.rid}).subscribe({
            next: () => {
                n.dismissed = false;
                this.buildTabs();
            },
            error: () => this.toaster.pop('error', this.transloco.translate('kobeac.notices'), this.transloco.translate('kobeac.unhide_failed')),
        });
    }

    formatDateTime(millis: number): string {
        if (!millis || millis <= 0) {
            return '';
        }
        const d = new Date(millis);
        const p = (n: number) => (n < 10 ? '0' + n : '' + n);
        return d.getFullYear() + '/' + p(d.getMonth() + 1) + '/' + p(d.getDate())
            + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
    }
}
